import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Sun, TrendingUp, Moon, Heart, Apple } from 'lucide-react';
import LunarEnergyTracker from '../LunarEnergyTracker';
import LunarWisdomTeachings from '../LunarWisdomTeachings';
import TrendsAnalysis from '../TrendsAnalysis';
import { TherappiSupportCard } from '../journey/TherappiSupportCard';
import { NutritionalGuidanceCard } from '../journey/NutritionalGuidanceCard';
import TherapyTokenGift from './TherapyTokenGift';
import AppointmentManager from './AppointmentManager';
import { calculateLunarPhase } from '@/utils/lunarPhases';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface MenopauseTrackerProps {
  onBack: () => void;
}

export default function MenopauseTracker({ onBack }: MenopauseTrackerProps) {
  const [showTrends, setShowTrends] = useState(false);
  const { toast } = useToast();
  const currentLunarPhase = calculateLunarPhase(new Date());

  const handleEnergyTrackerSubmit = async (data: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('daily_checkins')
        .upsert({
          user_id: user.id,
          date: data.date,
          ...data,
          is_menopausal: true
        }, {
          onConflict: 'user_id,date'
        });

      toast({
        title: "Saved! ☀️",
        description: "Your wellness tracking has been recorded.",
      });
    } catch (error) {
      console.error('Error saving:', error);
      throw error;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-3xl font-bold">Menopause & Beyond</h2>
          <p className="text-muted-foreground">Track symptoms and align with lunar energy</p>
        </div>
      </div>

      {/* Educational Card */}
      <Card className="bg-gradient-to-br from-soft-accent/10 to-accent/10 border-soft-accent/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sun className="h-5 w-5" />
            Your Lunar-Guided Journey
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm">
            After menopause, your body no longer follows a menstrual cycle, but you still experience natural rhythms. 
            By tracking the lunar cycle, you can understand your energy fluctuations, honor your body's wisdom, 
            and navigate symptoms with awareness and grace.
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-semibold text-primary mb-2">What This Tracker Does:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Tracks symptoms daily</li>
                <li>• Follows lunar energy phases</li>
                <li>• Identifies patterns</li>
                <li>• Provides self-care guidance</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-primary mb-2">Why It Helps:</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Understand energy patterns</li>
                <li>• Plan activities mindfully</li>
                <li>• Manage symptoms better</li>
                <li>• Feel empowered & connected</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      <TherapyTokenGift journeyType="menopause" />

      {/* Main Tabs */}
      <Tabs defaultValue="energy" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="energy">
            <Sun className="h-4 w-4 mr-2" />
            Daily Tracking
          </TabsTrigger>
          <TabsTrigger value="wisdom">
            <Moon className="h-4 w-4 mr-2" />
            Lunar Wisdom
          </TabsTrigger>
          <TabsTrigger value="trends">
            <TrendingUp className="h-4 w-4 mr-2" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="support">
            <Heart className="h-4 w-4 mr-2" />
            Support
          </TabsTrigger>
          <TabsTrigger value="nutrition">
            <Apple className="h-4 w-4 mr-2" />
            Nutrition
          </TabsTrigger>
        </TabsList>

        <TabsContent value="energy" className="space-y-4">
          <LunarEnergyTracker
            menopauseStage="menopause"
            onSubmit={handleEnergyTrackerSubmit}
          />
          <AppointmentManager journeyType="menopause" />
        </TabsContent>

        <TabsContent value="wisdom" className="space-y-4">
          <LunarWisdomTeachings lunarPhase={currentLunarPhase} />
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Wellness Trends & Patterns</CardTitle>
            </CardHeader>
            <CardContent>
              <Button onClick={() => setShowTrends(true)} className="w-full">
                <TrendingUp className="h-4 w-4 mr-2" />
                View Detailed Analysis
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="support" className="space-y-4">
          <TherappiSupportCard journeyType="menopause" />
        </TabsContent>

        <TabsContent value="nutrition" className="space-y-4">
          <NutritionalGuidanceCard focusArea="menopause" />
        </TabsContent>
      </Tabs>

      {showTrends && <TrendsAnalysis onClose={() => setShowTrends(false)} />}
    </div>
  );
}
